import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import * as L from 'leaflet';
import { environment } from 'src/environments/environment';
import { createPixiPointsLayer, PixiPoint, PixiRadius } from './pixi-points-layer';

/**
 * Layer-list entry for a Pixi point layer (type: LAYER_TYPES.PIXI_LAYER). Uses the same keys as the
 * WMS/GeoJSON entries, plus pixi_* keys naming methods on the host service (like pointToLayer/onEachFeature).
 */
export interface PixiLayerConfig {
  layeroptions_name: string;           // 'WORKSPACE:LAYER'
  environment_server_url: string;      // key into environment.url
  cql_filter?: string;
  layeroptions_pane?: string;
  visible?: boolean;
  pixi_color: string;                  // host method (props) => '#rrggbb' | null (null hides the point)
  pixi_popup?: string;                 // host method (props) => popup HTML | null
  pixi_popup_pane?: string;            // pane for the popup, e.g. 'pixiPopup' to keep it above other layers
  pixi_radius?: PixiRadius;            // screen px, or zoom -> px stops e.g. { 5: 2, 8: 3, 11: 5 }
  pixi_border_color?: string;          // '#rrggbb'
  pixi_border_width?: number;          // screen px; 0 for no outline
  pixi_property_names?: string[];      // WFS attributes to fetch; omit to fetch all
  [key: string]: any;
}

// Recently fetched datasets, so toggling a layer or switching back to a time range doesn't refetch
const MAX_CACHED_REQUESTS = 10;

@Injectable({
  providedIn: 'root'
})
export class PixiLayerService {

  private pointsCache = new Map<string, PixiPoint[]>();

  constructor(private http: HttpClient) { }

  /** Fetches the config's points and builds its layer (not yet added to the map) */
  loadLayer(config: PixiLayerConfig, host: any): Observable<L.Layer> {
    return this.loadWfsPoints(config).pipe(
      map(points => createPixiPointsLayer(points, {
        ...config,
        ...this.definedOnly({
          pane: config.layeroptions_pane,
          popupPane: config.pixi_popup_pane,
          radius: config.pixi_radius,
          borderColor: config.pixi_border_color,
          borderWidth: config.pixi_border_width
        }),
        getColor: this.hostMethod(host, config.pixi_color),
        getPopupContent: config.pixi_popup ? this.hostMethod(host, config.pixi_popup) : null
      }))
    );
  }

  loadWfsPoints(config: PixiLayerConfig): Observable<PixiPoint[]> {
    const [workspace] = config.layeroptions_name.split(':');
    const url = `${environment.url[config.environment_server_url]}${workspace}/ows`;
    let params = new HttpParams()
      .set('service', 'WFS')
      .set('version', '1.0.0')
      .set('request', 'GetFeature')
      .set('typeName', config.layeroptions_name)
      .set('outputFormat', 'application/json')
      .set('srsName', 'EPSG:4326');
    if (config.cql_filter) {
      params = params.set('CQL_FILTER', config.cql_filter);
    }
    if (config.pixi_property_names) {
      params = params.set('propertyName', config.pixi_property_names.join(','));
    }

    const cacheKey = `${url}?${params.toString()}`;
    if (this.pointsCache.has(cacheKey)) {
      return of(this.pointsCache.get(cacheKey));
    }
    return this.http.get(url, { params, responseType: 'text' }).pipe(
      mergeMap(text => this.parseGeoJsonPoints(text, config.layeroptions_name)),
      tap(points => this.cachePoints(cacheKey, points))
    );
  }

  // GeoServer answers 200 with an XML exception for a bad layer name or CQL field, so check before parsing
  private parseGeoJsonPoints(text: string, layerName: string): Observable<PixiPoint[]> {
    if (text.trim().startsWith('<')) {
      const match = text.match(/<ows:ExceptionText>([\s\S]*?)<\/ows:ExceptionText>|<ServiceException[^>]*>([\s\S]*?)<\/ServiceException>/);
      const reason = match ? (match[1] || match[2]).trim() : 'XML returned instead of GeoJSON';
      return throwError(new Error(`${layerName}: ${reason}`));
    }
    const points: PixiPoint[] = [];
    (JSON.parse(text).features || []).forEach((feature: any) => {
      const geometry = feature.geometry;
      if (!geometry) {
        return;
      }
      const coordinates = geometry.type === 'Point' ? [geometry.coordinates]
        : geometry.type === 'MultiPoint' ? geometry.coordinates : [];
      coordinates.forEach((c: number[]) => points.push({ lat: c[1], lng: c[0], props: feature.properties || {} }));
    });
    return of(points);
  }

  private cachePoints(key: string, points: PixiPoint[]) {
    if (this.pointsCache.size >= MAX_CACHED_REQUESTS) {
      this.pointsCache.delete(this.pointsCache.keys().next().value);
    }
    this.pointsCache.set(key, points);
  }

  // Unset config keys must not override the layer's own defaults with undefined
  private definedOnly(options: { [key: string]: any }) {
    const defined: { [key: string]: any } = {};
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined && options[key] !== null) {
        defined[key] = options[key];
      }
    });
    return defined;
  }

  private hostMethod(host: any, name: string) {
    if (typeof host[name] !== 'function') {
      throw new Error(`Pixi layer config refers to missing method '${name}'`);
    }
    return (props: any) => host[name](props);
  }
}
